<template>
  <div class="page-container">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <PageHeader
      title="Namudekeralam Applications"
      subtitle="Applications paid through Nammude Keralam."
      icon="mdi-cash-check"
      :count="totalLength"
    ></PageHeader>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          v-model="search"
          class="filter-bar-search"
          outlined
          dense
          hide-details
          clearable
          label="Application no, applicant or production house"
          prepend-inner-icon="mdi-magnify"
          @input="searchChanged"
          @click:clear="clearSearch"
        ></v-text-field>
      </div>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!rows.length" class="empty-state">
        <v-icon size="52">mdi-cash-remove</v-icon>
        <div class="cell-strong">{{ search ? "No applications match this search" : "No Namudekeralam applications yet" }}</div>
        <div>{{ search ? "Change the search." : "Applications appear here once they are paid through Nammude Keralam." }}</div>
      </div>

      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Application status</th>
              <th>Latest payment</th>
              <th>Amount</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r._id">
              <td data-label="Application">
                <div>
                  <div class="cell-strong">{{ r.applicantName || "—" }}</div>
                  <div v-if="r.applicationNo" class="cell-muted">#{{ r.applicationNo }}</div>
                  <div v-if="r.nameofProductionhouse" class="cell-muted">{{ r.nameofProductionhouse }}</div>
                  <div v-if="r.divisionid && r.divisionid.officeName" class="cell-muted">{{ r.divisionid.officeName }}</div>
                </div>
              </td>
              <td data-label="Application status">
                <span class="status-pill">{{ r.status || "—" }}</span>
              </td>
              <td data-label="Latest payment">
                <div v-if="r.namudekeralamPayment">
                  <span class="status-pill" :class="'status-pill--' + tone(r.namudekeralamPayment)">{{ statusLabel(r.namudekeralamPayment) }}</span>
                  <div v-if="r.namudekeralamPayment.created_at" class="cell-muted">{{ formatDateTime(r.namudekeralamPayment.created_at) }}</div>
                </div>
                <span v-else class="cell-muted">—</span>
              </td>
              <td data-label="Amount">
                <span v-if="r.namudekeralamPayment" class="pay-amount">₹{{ money(r.namudekeralamPayment.totalAmount) }}</span>
                <span v-else class="cell-muted">—</span>
              </td>
              <td data-label="Reference">
                <div v-if="r.namudekeralamPayment" class="ref-list">
                  <div v-if="r.namudekeralamPayment.departmentId"><span class="ref-key">Dept ID</span><span class="ref-val">{{ r.namudekeralamPayment.departmentId }}</span></div>
                  <div v-if="r.namudekeralamPayment.GRN"><span class="ref-key">GRN</span><span class="ref-val">{{ r.namudekeralamPayment.GRN }}</span></div>
                  <div v-if="r.namudekeralamPayment.CIN"><span class="ref-key">CIN</span><span class="ref-val">{{ r.namudekeralamPayment.CIN }}</span></div>
                </div>
                <span v-else class="cell-muted">—</span>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="pages > 1" class="pagination-bar">
        <v-pagination v-model="page" :length="pages" :total-visible="7" color="#15803d"></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";

const PAGE_SIZE = 10;

const STATUS_TONE = { Success: "success", Failed: "danger", Initiated: "warning", Cancelled: "neutral", Refundable: "info" };
const STATUS_LABEL = {
  Success: "Paid",
  Failed: "Failed",
  Initiated: "Waiting for payment",
  Cancelled: "Replaced by a newer attempt",
  Refundable: "Refundable",
};

// Super admin only: applications whose payment went through Nammude Keralam (/admin/namudekeralam/applications)
export default {
  name: "NamudekeralamApplications",
  components: { PageHeader },
  data() {
    return {
      rows: [],
      totalLength: 0,
      loading: false,
      search: "",
      page: 1,
      msg: "",
      showSnackBar: false,
      searchTimer: null,
      requestNo: 0,
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.totalLength / PAGE_SIZE);
    },
  },
  watch: {
    page() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    load() {
      clearTimeout(this.searchTimer);
      const requestNo = ++this.requestNo;
      this.loading = true;
      axios
        .post(
          "/admin/namudekeralam/applications",
          {
            page: this.page,
            limit: PAGE_SIZE,
            search: (this.search || "").trim() || undefined,
          },
          { headers: { token: localStorage.getItem("token") } }
        )
        .then((response) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          if (response.data && response.data.status) {
            this.rows = response.data.data || [];
            this.totalLength = response.data.totalLength || 0;
          } else {
            this.rows = [];
            this.totalLength = 0;
            this.toast((response.data && response.data.msg) || "Could not load the applications.");
          }
        })
        .catch((err) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          this.rows = [];
          this.totalLength = 0;
          this.toast("Could not load the applications. Please try again.");
          console.log(err);
        });
    },
    reload() {
      if (this.page !== 1) this.page = 1; // the watcher loads
      else this.load();
    },
    searchChanged() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 400);
    },
    clearSearch() {
      this.search = "";
      this.reload();
    },
    tone(p) {
      return STATUS_TONE[p.status] || "neutral";
    },
    statusLabel(p) {
      return STATUS_LABEL[p.status] || p.status || "—";
    },
    money(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "0";
    },
    formatDateTime(d) {
      return d ? moment(d).format("DD MMM YYYY, hh:mm A") : "";
    },
  },
};
</script>

<style scoped>
.pay-amount {
  font-weight: 800;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ref-list {
  display: grid;
  gap: 2px;
  font-size: 13px;
}

.ref-key {
  display: inline-block;
  min-width: 58px;
  margin-right: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.ref-val {
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  color: #1e293b;
  word-break: break-all;
}
</style>
